import React, { Fragment, useEffect, useState } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import { Button, FormGroup, Input } from 'reactstrap';
import { useAnnouncementsUIContext } from '../AnnouncementsUIProvider';
import AnnouncementsTableColumns, { AnnouncementsColumns } from './AnnouncementsTableColumns';
import { AnnouncementsTableSearchBar } from './AnnouncementsTableSearchBar';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  getAnnouncementList,
  setAnnouncementFilters,
  toggleCheckedItemById,
} from '../../redux/actions';
import EyeIcon from 'mdi-react/EyeIcon';
import PencilIcon from 'mdi-react/PencilIcon';
import LoadingIcon from 'mdi-react/LoadingIcon';
import { defaultPaginationProps } from '@/shared/helpers';
import moment from 'moment';
import SanitizeHTML from '@/shared/components/SanitizeHTML';

const AnnouncementsTable = () => {
  const announcementsUIContext = useAnnouncementsUIContext();
  const dispatch = useDispatch();
  const {
    data: announcements,
    error,
    filters,
    loading,
    totalRecords,
    checkedAnnouncementIds,
  } = useSelector((state) => state.cadsAIAnnouncements, shallowEqual);
  const {
    pageSize: rowsPerPage,
    pageNumber: page,
    sortBy: orderBy,
    isSortOrderDescending,
  } = filters;

  const [columns, setColumns] = useState(0);

  useEffect(() => {
    setColumns(AnnouncementsColumns.length);
  }, [AnnouncementsColumns.length]);

  React.useEffect(() => {
    dispatch(getAnnouncementList(filters));
  }, [filters]);

  const handleRequestSort = (event, property) => {
    dispatch(
      setAnnouncementFilters({
        sortBy: property,
        isSortOrderDescending: !isSortOrderDescending,
      }),
    );
  };

  const renderActionButtons = (announcementId) => (
    <div className="actions">
      <Button
        className="icon mb-0"
        color="success"
        outline
        onClick={() => announcementsUIContext.openViewAnnouncementsDialog(announcementId)}
      >
        <p>
          <EyeIcon /> View
        </p>
      </Button>
      <Button
        className="icon mb-0"
        color="primary"
        outline
        onClick={() => announcementsUIContext.openEditAnnouncementsDialog(announcementId)}
      >
        <p>
          <PencilIcon /> Edit
        </p>
      </Button>
    </div>
  );

  const handleChangePage = (event, currentPage) => {
    dispatch(setAnnouncementFilters({ pageSize: rowsPerPage, pageNumber: currentPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(setAnnouncementFilters({ pageSize: +event.target.value, pageNumber: 1 }));
  };

  const emptyRows =
    page === Math.ceil(totalRecords / rowsPerPage) ? rowsPerPage - announcements.length : 0;

  return (
    <Fragment>
      <div className="material-table__wrap">
        <AnnouncementsTableSearchBar />
        <Table className="material-table">
          <AnnouncementsTableColumns
            order={isSortOrderDescending ? 'desc' : 'asc'}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={rowsPerPage}
          />
          <TableBody>
            {(loading || !!error) && (
              <TableRow>
                <TableCell colSpan={columns}>
                  {loading && (
                    <div className={'text-center overflow-hidden'}>
                      <div className="panel__refresh position-relative" style={{ height: 75 }}>
                        <LoadingIcon />
                      </div>
                    </div>
                  )}
                  <div className={'text-center'}>
                    <p className={!!error ? 'm-5' : ''}>{error || 'Loading announcements...'}</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {!!!announcements.length && !loading && !error && (
              <TableRow style={{ height: 120 }}>
                <TableCell colSpan={columns} align="center">
                  {filters.status === null && filters.search === ''
                    ? 'No Announcements'
                    : 'No Announcements with applied filters'}
                </TableCell>
              </TableRow>
            )}
            {announcements.map((announcement) => {
              return (
                <TableRow className="material-table" tabIndex={-1} key={announcement?.id}>
                  <TableCell align="center" className="material-table__cell">
                    <FormGroup>
                      <Input
                        type="checkbox"
                        value={announcement?.id}
                        defaultChecked={checkedAnnouncementIds.includes(announcement?.id)}
                        onChange={(e) => dispatch(toggleCheckedItemById(+e.target.value))}
                      />
                    </FormGroup>
                  </TableCell>
                  <TableCell className="material-table__cell" align="center">
                    <SanitizeHTML html={announcement?.titleHtml} />
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    {announcement?.activeFromDate
                      ? moment(announcement?.activeFromDate).format('DD/MM/yyyy')
                      : '-'}
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    {announcement?.activeToDate
                      ? moment(announcement?.activeToDate).format('DD/MM/yyyy')
                      : '-'}
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    {announcement?.isPublic ? 'Yes' : 'No'}
                  </TableCell>
                  <TableCell
                    align="center"
                    className="material-table__cell"
                    style={{ minWidth: 250 }}
                  >
                    {renderActionButtons(announcement?.id)}
                  </TableCell>
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow style={{ height: 49 * emptyRows }}>
                <TableCell colSpan={columns} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page - 1}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        component={defaultPaginationProps.component}
        backIconButtonProps={defaultPaginationProps.backIconButton}
        nextIconButtonProps={defaultPaginationProps.nextIconButton}
        rowsPerPageOptions={defaultPaginationProps.rowsPerPage}
        className={defaultPaginationProps.className}
        dir={defaultPaginationProps.dir}
      />
    </Fragment>
  );
};

export default AnnouncementsTable;
